import { useEffect, useRef } from "react";

export type ConfirmVariant = "primary" | "danger";

interface InputProps {
  /** Label above the input field. */
  label: string;
  /** Current value (controlled). */
  value: string;
  /** Called on every change. */
  onChange: (next: string) => void;
  placeholder?: string;
  /** Optional small hint under the field. */
  hint?: string;
}

interface Props {
  open: boolean;
  title: string;
  message: string;
  confirmLabel?: string;
  cancelLabel?: string;
  variant?: ConfirmVariant;
  onConfirm: () => void;
  onCancel: () => void;
  /** When true, disables the confirm button (e.g. while an action is in flight). */
  busy?: boolean;
  /** Optional text input (e.g. archive label). */
  input?: InputProps;
}

/**
 * Inline confirm modal in the SVO eval-board look. Replaces native
 * window.confirm() so it doesn't look cheap on the beamer in front of
 * 64 people.
 *
 * Esc → cancel, Enter → confirm. Backdrop click → cancel.
 */
export function ConfirmDialog({
  open,
  title,
  message,
  confirmLabel = "Bestätigen",
  cancelLabel = "Abbrechen",
  variant = "primary",
  onConfirm,
  onCancel,
  busy = false,
  input,
}: Props) {
  const confirmBtnRef = useRef<HTMLButtonElement | null>(null);
  const inputRef = useRef<HTMLInputElement | null>(null);

  useEffect(() => {
    if (!open) return;
    // If we have an input, focus that first; otherwise the confirm button.
    if (input) inputRef.current?.focus();
    else confirmBtnRef.current?.focus();
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        e.preventDefault();
        onCancel();
      } else if (e.key === "Enter") {
        // Don't hijack Enter when caret is in the input — let it submit
        // through the dialog's own button instead of double-firing.
        const target = e.target as HTMLElement | null;
        if (target?.tagName === "INPUT" || target?.tagName === "TEXTAREA") {
          if (target?.tagName === "INPUT" && !busy) {
            e.preventDefault();
            onConfirm();
          }
          return;
        }
        e.preventDefault();
        if (!busy) onConfirm();
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onCancel, onConfirm, busy, input]);

  if (!open) return null;

  const confirmKlass = variant === "danger" ? "btn btn-danger" : "btn btn-primary";

  return (
    <div
      className="confirm-dialog-backdrop"
      onClick={(e) => {
        if (e.target === e.currentTarget) onCancel();
      }}
      role="dialog"
      aria-modal="true"
      aria-labelledby="confirm-dialog-title"
    >
      <div className="confirm-dialog-card">
        <div
          className="deck-eyebrow"
          style={{ color: "var(--svo-blau)", marginBottom: 6 }}
        >
          Bestätigung
        </div>
        <h2
          id="confirm-dialog-title"
          className="deck-title"
          style={{ fontSize: "clamp(1.3rem, 2.4vw, 1.7rem)", margin: "0 0 12px" }}
        >
          {title}
        </h2>
        <p
          className="deck-sub"
          style={{
            margin: "0 0 20px",
            whiteSpace: "pre-line",
            color: "rgba(255,255,255,0.78)",
          }}
        >
          {message}
        </p>
        {input && (
          <div className="confirm-dialog-input">
            <label className="confirm-dialog-input-label">{input.label}</label>
            <input
              ref={inputRef}
              type="text"
              className="input-text"
              value={input.value}
              onChange={(e) => input.onChange(e.target.value)}
              placeholder={input.placeholder}
              disabled={busy}
            />
            {input.hint && (
              <small className="confirm-dialog-input-hint">{input.hint}</small>
            )}
          </div>
        )}
        <div className="confirm-dialog-actions">
          <button
            type="button"
            onClick={onCancel}
            disabled={busy}
            className="btn btn-ghost btn-lg"
          >
            {cancelLabel}
          </button>
          <button
            ref={confirmBtnRef}
            type="button"
            onClick={onConfirm}
            disabled={busy}
            className={`${confirmKlass} btn-lg`}
            aria-busy={busy}
          >
            {busy ? "…" : confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
}
